import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Group, Modal, Select, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";

import { handleOnChange } from "@utils/handleOnChange";

import {
  getMedicalHistoryOriginValues,
  getMedicalHistoryStatusValues,
} from "@data/dropdownValues";

import { useCreateMedicalRecord } from "@hooks/mutations/patient/useCreateMedicalRecord";

import { PatientMedicalHistoryItem } from "@customTypes/patient";

import { StyledAddAllergyModalContent } from "../AddAllergyModal/AddAllergyModal.styled";


interface AddMedicalHistoryModalProps {
  opened: boolean;
  close: () => void;
}
export const AddMedicalHistoryModal = ({
  opened,
  close,
}: AddMedicalHistoryModalProps) => {
  const { t } = useTranslation();

  const { patientId } = useParams();
  const [date, setDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  const [formData, setFormData] = useState({} as PatientMedicalHistoryItem);
  const { mutate: createMedicalRecord, isLoading: isCreateMRLoading } =
    useCreateMedicalRecord();

  const handleAddMedicalRecord = () => {
    if (!formData.condition || !date || !status || !origin) return;

    const newFormData = { ...formData, patientId, date: date as Date };
    // console.log(newFormData);
    createMedicalRecord(newFormData, {
      onSuccess: () => {
        setFormData({} as PatientMedicalHistoryItem);
        setDate(null);
        setOrigin(null);
        setStatus(null);
        close();
      },
    });
    // console.log("Adding surgical record");
  };

  useEffect(() => {
    if (status) {
      setFormData({ ...formData, status });
    }
  }, [status]);

  useEffect(() => {
    if (origin) {
      setFormData({
        ...formData,
        origin: origin as PatientMedicalHistoryItem["origin"],
      });
    }
  }, [origin]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-medical-history")}</Text>}
      opened={opened}
      onClose={close}
      styles={{ body: { minHeight: "auto" } }}
      // scrollAreaComponent={ScrollAreaAutosize}
    >
      <StyledAddAllergyModalContent>
        <form className="form">
          <div className="form-field">
            <label htmlFor="name">{t("medical-condition")}</label>
            <input
              type="text"
              id="name"
              placeholder={t("eg", { example: t("hypertension") })}
              name="condition"
              onChange={e => handleOnChange(e, setFormData, formData)}
            />
          </div>

          <div className="form-field">
            <DatePickerInput
              label={t("date")}
              value={date}
              onChange={setDate}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
            />
          </div>

          <div className="form-group">
            <div className="form-field">
              <Select
                label={t("status")}
                placeholder={t("select-status")}
                data={getMedicalHistoryStatusValues()}
                value={formData.status}
                onChange={setStatus}
              />
            </div>
            <div className="form-field">
              <Select
                label={t("origin")}
                placeholder={t("select-origin")}
                data={getMedicalHistoryOriginValues()}
                value={formData.origin}
                onChange={setOrigin}
              />
            </div>
          </div>
        </form>

        <Group mt="xl" justify="end">
          <PermissionGate permission="consultations.create"><Button
            onClick={handleAddMedicalRecord}
            disabled={!formData.condition || !date || !status || !origin}
            loading={isCreateMRLoading}>
            {t("add-record")}
          </Button></PermissionGate>
        </Group>
      </StyledAddAllergyModalContent>
    </Modal>
  );
};
